import React, { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { 
  QrCode, 
  Copy, 
  Download, 
  RefreshCw, 
  Loader2, 
  Share2,
  Check,
  Maximize2
} from 'lucide-react';

export default function QRShareSection() {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const [connectUrl, setConnectUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const qrRef = useRef(null);

  const generateQRCode = async () => {
    setLoading(true);
    try {
      const response = await api.post('/users/me/connect-token');
      // Build full URL
      const fullUrl = `${window.location.origin}${response.connectUrl}`;
      setConnectUrl(fullUrl);
    } catch (err) {
      toast.error(err.message || 'Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const regenerateQRCode = async () => {
    setLoading(true);
    try {
      const response = await api.delete('/users/me/connect-token');
      const fullUrl = `${window.location.origin}${response.connectUrl}`;
      setConnectUrl(fullUrl);
      toast.success(t('qr.regenerated') || 'QR code regenerated. Old links are now invalid.');
    } catch (err) {
      toast.error(err.message || 'Failed to regenerate QR code');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!connectUrl) return;
    
    try {
      await navigator.clipboard.writeText(connectUrl);
      setCopied(true);
      toast.success(t('qr.linkCopied') || 'Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const downloadQR = () => {
    if (!qrRef.current) return;
    
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    // Create canvas from SVG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 512;
      canvas.height = 512;
      
      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code
      ctx.drawImage(img, 0, 0, 512, 512);
      
      // Download
      const link = document.createElement('a');
      link.download = `pocketfiler-qr-${user?.name?.replace(/\s+/g, '-') || 'code'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const shareLink = async () => {
    if (!connectUrl) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Connect with me on PocketFiler',
          text: `Connect with ${user?.name || 'me'} on PocketFiler`,
          url: connectUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyLink(); // Fallback to copy
        }
      }
    } else {
      copyLink(); // Fallback to copy
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            {t('qr.title') || 'Share your PocketFiler QR'}
          </CardTitle>
          <CardDescription>
            {t('qr.description') || 'Show this code to clients or partners. When they scan it, they can connect with you and start a deal in seconds.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!connectUrl ? (
            <Button 
              onClick={generateQRCode} 
              disabled={loading}
              className="w-full min-h-[48px]"
              data-testid="generate-qr-button"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <QrCode className="h-4 w-4 mr-2" />
              )}
              {t('qr.generate') || 'Generate QR Code'}
            </Button>
          ) : (
            <div className="space-y-4">
              {/* QR Code Display */}
              <div 
                ref={qrRef}
                className="bg-card p-4 rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted transition-colors"
                onClick={() => setShowFullScreen(true)}
                role="button"
                aria-label="Click to view full screen"
              >
                <QRCodeSVG 
                  value={connectUrl}
                  size={192}
                  level="M"
                  includeMargin={true}
                  bgColor="#ffffff"
                  fgColor="#0D7377"
                />
              </div>

              {/* Expand hint */}
              <p className="text-xs text-center text-muted-foreground">
                <Maximize2 className="h-3 w-3 inline mr-1" />
                {t('qr.clickToExpand') || 'Click to view full screen'}
              </p>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyLink}
                  className="min-h-[44px]"
                  data-testid="copy-link-button"
                >
                  {copied ? (
                    <Check className="h-4 w-4 mr-2 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  {copied ? (t('qr.copied') || 'Copied!') : (t('qr.copyLink') || 'Copy Link')}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={shareLink}
                  className="min-h-[44px]"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  {t('qr.share') || 'Share'}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadQR}
                  className="min-h-[44px]"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('qr.download') || 'Download'}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={regenerateQRCode}
                  disabled={loading}
                  className="min-h-[44px]"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {t('qr.regenerate') || 'Reset'}
                </Button>
              </div>

              {/* URL Display */}
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">{t('qr.yourLink') || 'Your connect link:'}</p>
                <p className="text-sm font-mono break-all">{connectUrl}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full Screen QR Modal */}
      <Dialog open={showFullScreen} onOpenChange={setShowFullScreen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              {t('qr.scanToConnect') || 'Scan to Connect'}
            </DialogTitle>
            <DialogDescription className="text-center">
              {user?.name || 'PocketFiler User'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center py-4">
            <div className="bg-card p-6 rounded-xl shadow-lg">
              <QRCodeSVG 
                value={connectUrl || ''}
                size={256}
                level="M"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#0D7377"
              />
            </div>
            
            <p className="text-sm text-muted-foreground mt-4 text-center">
              {t('qr.scanInstructions') || 'Scan this QR with PocketFiler or any QR scanner to connect.'}
            </p>

            {user?.preferred_language && (
              <p className="text-xs text-muted-foreground mt-2">
                {t('qr.primaryLanguage') || 'Primary language'}: {user.preferred_language.toUpperCase()}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 min-h-[48px]"
              onClick={copyLink}
            >
              {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            <Button
              variant="outline"
              className="flex-1 min-h-[48px]"
              onClick={downloadQR}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
